import React from "react";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from "@apollo/client";
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/function";
import { ToastContainer } from "react-toastify";

import { Footer } from "src/components/footer/footer";
import { Navbar } from "src/components/navbar/navbar";
import { ErrorBoundary } from "src/error_boundary";
import { AboutPage } from "src/pages/about/about_page";
import { AccountPage } from "src/pages/account/account_page";
import { CreateUserPage } from "src/pages/admin/create_user_page";
import { CreateSchoolPage } from "src/pages/admin/create_school_page";
import { CreateReportPage } from "src/pages/admin/create_report_page";
import { EditSchoolPage } from "src/pages/admin/edit_school_page";
import { ConstituentGroupsPage } from "src/pages/constituent_groups/constituent_groups_page";
import { DashboardPage } from "src/pages/dashboard/dashboard_page";
import { ReportsHomePage } from "src/pages/reports/reports_home_page";
import { ReportFullPage } from "src/pages/reports/report_full_page";
import { ReportsFullPage } from "src/pages/reports/reports_full_page";
import { LoginPage } from "src/pages/login/login_page";
import { NotFoundPage } from "src/pages/not_found/not_found_page";
import { PromotionPage } from "src/pages/promotion/promotion_page";
import { ReportsPage } from "src/pages/reports/reports_page";
import { READ_COOKIE_REQUEST } from "src/state/ducks/auth";
import store from "src/state/store";
import { ProtectedRoute } from "src/utils/routes";
import { readCookie } from "src/utils/storage";

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
});

const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_ENDPOINT ?? "",
    headers: {
        "X-Parse-Application-Id": process.env.REACT_APP_X_PARSE_APP_ID ?? "",
        "X-Parse-Session-Token": pipe(
            readCookie("fsci")(),
            E.map((cookie) => cookie.token),
            E.getOrElse(() => ""),
        ),
    },
});

const client = new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache(),
});

store.dispatch({
    type: READ_COOKIE_REQUEST,
});

export const App = (): React.ReactElement => {
    return (
        <React.StrictMode>
            <ErrorBoundary>
                <Provider store={store}>
                    <ApolloProvider client={client}>
                        <BrowserRouter>
                            <ToastContainer
                                autoClose={3000}
                                closeOnClick
                                draggable
                                hideProgressBar={false}
                                newestOnTop={false}
                                pauseOnHover
                                position="top-right"
                                rtl={false}
                            />
                            <Navbar />
                            <Switch>
                                <ProtectedRoute component={DashboardPage} exact path="/" />
                                <Route component={AboutPage} exact path="/about" />
                                <ProtectedRoute component={AccountPage} exact path="/account" />
                                <ProtectedRoute component={CreateReportPage} exact path="/admin/create-report" />
                                <ProtectedRoute component={CreateSchoolPage} exact path="/admin/create-school" />
                                <ProtectedRoute component={CreateUserPage} exact path="/admin/create-user" />
                                <ProtectedRoute component={EditSchoolPage} exact path="/admin/edit-school" />
                                <Route component={LoginPage} exact path="/auth" />
                                <ProtectedRoute component={ConstituentGroupsPage} exact path="/constituents" />
                                <ProtectedRoute component={PromotionPage} exact path="/promotion" />
                                <ProtectedRoute component={ReportsHomePage} exact path="/reports" />
                                <ProtectedRoute component={ReportFullPage} exact path="/reports/:type/:year/print" />
                                <ProtectedRoute component={ReportsFullPage} exact path="/reports/:type/print" />
                                <ProtectedRoute component={ReportsPage} path="/reports/:type/:year?/:section" />
                                <Route component={NotFoundPage} path="*" />
                            </Switch>
                            <Footer />
                        </BrowserRouter>
                    </ApolloProvider>
                </Provider>
            </ErrorBoundary>
        </React.StrictMode>
    );
};
